/**
 * daddl-cart.js
 * -----------------------------------------------------------------------------
 * Custom, app-free enhancements for the Dawn cart drawer:
 *   1. <daddl-cart-rewards>  – a milestone / reward progress bar
 *                              (free shipping, discount tiers, …).
 *   2. <daddl-cart-upsells>  – a "recommended products" grid with one-tap
 *                              add-to-cart, wired into Dawn's section rendering.
 *   3. <daddl-cart-timer>    – a session-persistent "items reserved" countdown.
 *
 * Design notes for future developers
 * ----------------------------------
 * - These are self-contained Web Components. Dawn re-renders the cart drawer
 *   HTML on every cart mutation (see `cart.js` / `cart-drawer.js`), which
 *   destroys and recreates our elements. Because the logic lives in
 *   `connectedCallback` / `disconnectedCallback`, each component transparently
 *   re-initialises after any re-render — no manual re-binding required.
 * - The reward bar's fill is ALSO rendered server-side (inline width) by
 *   snippets/daddl-cart-rewards.liquid, so it is correct even before/without JS
 *   and on every server re-render. The JS here keeps it live between renders.
 * - Configuration is passed from Liquid via `data-*` attributes so the same
 *   compiled asset works for any shop/locale/market without edits.
 * - No external dependencies; safe to load once (guarded custom-element
 *   registration).
 * -----------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /**
   * @typedef {Object} ShopifyCartItem
   * @property {number} id           Variant id.
   * @property {number} product_id   Parent product id.
   * @property {number} quantity     Quantity in cart.
   * @property {Object} properties   Line-item properties.
   *
   * @typedef {Object} ShopifyCart
   * @property {number} total_price          Cart total in the presentment currency's minor units.
   * @property {number} item_count           Total number of items.
   * @property {string} [currency]           Presentment currency ISO code (e.g. "AED", "EUR").
   * @property {ShopifyCartItem[]} items     Line items.
   */

  /* ---------------------------------------------------------------------------
   * Shared utilities
   * ------------------------------------------------------------------------- */

  /**
   * Subscribe to Dawn's cart-update pub/sub event, resiliently.
   * @param {(cart: ShopifyCart | null) => void} callback Invoked with cart data (or null when unknown).
   * @returns {() => void} Unsubscribe function.
   */
  function onCartUpdate(callback) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var cart = event && event.cartData && typeof event.cartData.total_price === 'number' ? event.cartData : null;
        callback(cart);
      });
    }
    var handler = function () {
      callback(null);
    };
    document.addEventListener('daddl:cart:refresh', handler);
    return function () {
      document.removeEventListener('daddl:cart:refresh', handler);
    };
  }

  /**
   * Resolve cart data, preferring an event payload and falling back to /cart.js.
   * @param {ShopifyCart | null} maybeCart Cart data from an event, if available.
   * @returns {Promise<ShopifyCart | null>}
   */
  function resolveCart(maybeCart) {
    if (maybeCart && typeof maybeCart.total_price === 'number') {
      return Promise.resolve(maybeCart);
    }
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * The amount reward progress is measured against: the subtotal AFTER
   * line-level discounts but BEFORE cart-level discounts. Using total_price
   * would make an unlocked "10% off" tier lower the measured amount and drag
   * the bar back below the milestone that triggered it.
   * @param {ShopifyCart} cart
   * @returns {number} Amount in minor units.
   */
  function progressAmount(cart) {
    return typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : cart.total_price;
  }

  /**
   * Format a minor-unit amount (e.g. 1500) into a human string ("15 AED").
   * @param {number} minorUnits Amount in the shop's smallest currency unit.
   * @param {string} currency   Currency label appended to the value.
   * @returns {string}
   */
  function formatAmount(minorUnits, currency) {
    var major = minorUnits / 100;
    var value = Number.isInteger(major) ? String(major) : major.toFixed(2);
    return currency ? value + ' ' + currency : value;
  }

  /**
   * Register a custom element only once (safe against duplicate script loads).
   * @param {string} tag Custom element tag name.
   * @param {CustomElementConstructor} ctor Element class.
   */
  function defineElement(tag, ctor) {
    if (!customElements.get(tag)) {
      customElements.define(tag, ctor);
    }
  }

  /* ---------------------------------------------------------------------------
   * Confetti — celebratory burst when a reward tier is unlocked
   * ------------------------------------------------------------------------- */

  var CONFETTI_COLORS = ['#611c47', '#ef8da4', '#2e9e5a', '#f3c045', '#ffffff'];

  /**
   * Fire a short confetti burst centred on an element (e.g. the milestone badge
   * that was just unlocked). Self-contained: creates a fixed, pointer-transparent
   * canvas above the drawer and removes it when the animation ends.
   * @param {HTMLElement | null} element Origin element; falls back to the upper
   *   middle of the viewport when missing or not visible.
   */
  function confettiBurstFrom(element) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var originX = window.innerWidth / 2;
    var originY = window.innerHeight * 0.3;
    if (element) {
      var rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        originX = rect.left + rect.width / 2;
        originY = rect.top + rect.height / 2;
      }
    }

    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483646;';
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    document.body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    var particles = [];
    for (var i = 0; i < 220; i += 1) {
      var angle = Math.random() * Math.PI * 2;
      var speed = 5 + Math.random() * 12;
      particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 8, // bias the burst upward
        w: 6 + Math.random() * 6,
        h: 4 + Math.random() * 5,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 0.35,
        swayPhase: Math.random() * Math.PI * 2,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        life: 0,
        ttl: 130 + Math.random() * 90, // frames (~2.2–3.7s at 60fps)
      });
    }

    var startTime = Date.now();

    function frame() {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      var alive = false;
      for (var j = 0; j < particles.length; j += 1) {
        var p = particles[j];
        if (p.life >= p.ttl) continue;
        alive = true;

        p.vy += 0.18; // gentle gravity so pieces float longer
        p.vx *= 0.99; // drag
        p.x += p.vx + Math.sin(p.life * 0.09 + p.swayPhase) * 0.9; // flutter
        p.y += p.vy;
        p.rot += p.vrot;
        p.life += 1;

        // Hold full opacity for the first ~60% of life, then fade out.
        var progress = p.life / p.ttl;
        var fade = progress < 0.6 ? 1 : 1 - (progress - 0.6) / 0.4;
        ctx.globalAlpha = Math.max(0, Math.min(1, fade));
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        // Vertical squash simulates 3D tumbling of a paper strip.
        ctx.scale(1, 0.6 + 0.4 * Math.sin(p.life * 0.18 + p.swayPhase));
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      ctx.globalAlpha = 1;

      if (alive && Date.now() - startTime < 6000) {
        window.requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    }

    window.requestAnimationFrame(frame);
  }

  /**
   * Thresholds (minor units) known to be reached, shared across drawer
   * re-renders (the module survives even though the component is recreated on
   * every cart mutation). `null` until the first paint so confetti never fires
   * for tiers that were already unlocked when the page loaded.
   * @type {Set<number> | null}
   */
  var confettiBaseline = null;

  /**
   * Last painted fill width (%) of the rewards bar, shared across drawer
   * re-renders. Dawn replaces the drawer HTML on every cart change, so the
   * fill element is recreated already AT its new width and the CSS width
   * transition never fires. Remembering the previous width lets the new
   * element start there and animate smoothly to the new value.
   * @type {number | null}
   */
  var lastFillPercent = null;

  /* ---------------------------------------------------------------------------
   * <daddl-cart-rewards> — milestone / reward progress bar
   * ------------------------------------------------------------------------- */

  /**
   * Renders a horizontal progress bar with milestone nodes. Each node unlocks
   * when the cart total reaches its `data-threshold` (minor units). Node
   * positions + fill are computed identically to the server-side Liquid so the
   * two never disagree.
   */
  class DaddlCartRewards extends HTMLElement {
    connectedCallback() {
      /** @type {HTMLElement | null} */
      this.fillEl = this.querySelector('[data-daddl-rewards-fill]');
      /** @type {HTMLElement | null} */
      this.messageEl = this.querySelector('[data-daddl-rewards-message]');

      this.currency = this.dataset.currency || '';
      this.strAway = this.dataset.strAway || '';
      this.strComplete = this.dataset.strComplete || '';

      var nodes = Array.prototype.slice.call(this.querySelectorAll('[data-threshold]'));
      nodes.sort((a, b) => (parseInt(a.dataset.threshold, 10) || 0) - (parseInt(b.dataset.threshold, 10) || 0));

      /** @type {{ el: HTMLElement, threshold: number, label: string, position: number }[]} */
      this.milestones = nodes.map((el, index) => {
        var position = ((index + 1) / nodes.length) * 100;
        el.style.insetInlineStart = position + '%';
        return {
          el: el,
          threshold: parseInt(el.dataset.threshold, 10) || 0,
          label: el.dataset.label || '',
          position: position,
        };
      });

      this.update(parseInt(this.dataset.total, 10) || 0);
      this.refresh();

      this.unsubscribe = onCartUpdate((cart) => {
        if (cart) {
          this.applyCart(cart);
        } else {
          this.refresh();
        }
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /** Pull the live cart and repaint (source of truth). */
    refresh() {
      resolveCart(null).then((cart) => {
        if (cart) this.applyCart(cart);
      });
    }

    /**
     * Apply a resolved cart: keep the currency market-accurate, then repaint.
     * @param {ShopifyCart} cart
     */
    applyCart(cart) {
      if (cart.currency) this.currency = cart.currency;
      this.update(progressAmount(cart));
    }

    /**
     * Compute the fill width (%) for a cart total by interpolating linearly
     * between milestone anchor points, starting from (0, 0%).
     * @param {number} total Cart total in minor units.
     * @returns {number} Fill percentage (0–100).
     */
    computeFillPercent(total) {
      var prevAmount = 0;
      var prevPosition = 0;
      for (var i = 0; i < this.milestones.length; i += 1) {
        var milestone = this.milestones[i];
        if (total < milestone.threshold) {
          var span = milestone.threshold - prevAmount || 1;
          var ratio = (total - prevAmount) / span;
          return prevPosition + ratio * (milestone.position - prevPosition);
        }
        prevAmount = milestone.threshold;
        prevPosition = milestone.position;
      }
      return 100;
    }

    /**
     * Recompute node states, the fill, and the status message for a cart total.
     * @param {number} total Cart total in minor units.
     */
    update(total) {
      if (this.fillEl) {
        // The fill's critical styles (incl. an initial width) are inlined
        // server-side; here we keep the width live between server re-renders.
        var pct = Math.max(0, Math.min(100, this.computeFillPercent(total)));

        // Smooth animation across drawer re-renders: the recreated element is
        // already at its new width, so snap it back to the previous width
        // (transition off), force a reflow, then let the CSS transition carry
        // it to the new value.
        if (lastFillPercent !== null && Math.abs(lastFillPercent - pct) > 0.1) {
          this.fillEl.style.transition = 'none';
          this.fillEl.style.width = lastFillPercent + '%';
          void this.fillEl.offsetWidth;
          this.fillEl.style.transition = '';
        }

        this.fillEl.style.width = pct + '%';
        lastFillPercent = pct;
      }

      /** @type {{ threshold: number, label: string } | null} */
      var nextReward = null;
      var reachedNow = new Set();

      this.milestones.forEach((milestone) => {
        var reached = total >= milestone.threshold;
        milestone.el.classList.toggle('is-reached', reached);
        milestone.el.setAttribute('aria-disabled', String(!reached));
        if (reached) reachedNow.add(milestone.threshold);
        if (!reached && !nextReward) {
          nextReward = { threshold: milestone.threshold, label: milestone.label };
        }
      });

      this.celebrateNewTiers(reachedNow);

      if (!this.messageEl) return;

      if (nextReward) {
        var remaining = formatAmount(nextReward.threshold - total, this.currency);
        this.messageEl.innerHTML = this.strAway
          .replace('__AMOUNT__', '<strong>' + remaining + '</strong>')
          .replace('__REWARD__', '<strong>' + nextReward.label + '</strong>');
      } else {
        this.messageEl.textContent = this.strComplete;
      }
    }

    /**
     * Fire a confetti burst from each milestone that JUST became reached.
     * The very first paint only records the baseline (no confetti for tiers
     * that were already unlocked when the page loaded / drawer re-rendered).
     * @param {Set<number>} reachedNow Thresholds currently reached.
     */
    celebrateNewTiers(reachedNow) {
      if (confettiBaseline !== null) {
        var newlyReached = this.milestones.filter(
          (milestone) => reachedNow.has(milestone.threshold) && !confettiBaseline.has(milestone.threshold)
        );

        newlyReached.forEach((milestone, index) => {
          var badge = milestone.el.querySelector('.daddl-cart-rewards__badge');
          // Small delay so the drawer's open/re-render transition settles first
          // (the badge position is measured when the burst actually fires);
          // stagger when one add crosses several tiers at once.
          window.setTimeout(function () {
            confettiBurstFrom(badge);
          }, 350 + index * 300);
        });
      }
      confettiBaseline = reachedNow;
    }
  }

  /* ---------------------------------------------------------------------------
   * <daddl-cart-upsells> — recommended products with add-to-cart
   * ------------------------------------------------------------------------- */

  /**
   * Renders a grid of upsell products. Adding uses Shopify's AJAX cart API and
   * Dawn's section-rendering contract so the drawer refreshes exactly like a
   * native add. Cards for products already in the cart are hidden.
   */
  class DaddlCartUpsells extends HTMLElement {
    connectedCallback() {
      this.strError = this.dataset.strError || 'Try again';

      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);

      // Initial visibility is already correct (rendered server-side). Only react
      // to live cart changes that don't trigger a full drawer re-render.
      this.unsubscribe = onCartUpdate((cart) => {
        resolveCart(cart).then((resolved) => this.syncVisibility(resolved));
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.unsubscribe) this.unsubscribe();
    }

    /**
     * Hide upsell cards whose product is already in the cart; collapse the
     * component when nothing is left to recommend.
     * @param {ShopifyCart | null} cart
     */
    syncVisibility(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      var inCart = new Set(cart.items.map((item) => String(item.product_id)));
      var cards = this.querySelectorAll('[data-product-id]');
      var visible = 0;
      cards.forEach((card) => {
        var hidden = inCart.has(card.dataset.productId);
        card.hidden = hidden;
        if (!hidden) visible += 1;
      });
      this.classList.toggle('is-empty', visible === 0);
    }

    /**
     * Handle add-to-cart clicks via delegation.
     * @param {MouseEvent} event
     */
    handleClick(event) {
      var button = event.target.closest('[data-daddl-upsell-add]');
      if (!button) return;
      event.preventDefault();
      this.addVariant(button);
    }

    /**
     * Fallback drawer refresh: Dawn's own section re-fetch on cart-drawer-items.
     * Used when the add succeeded but the inline `sections` payload was
     * missing/unusable (Shopify occasionally returns null for a section).
     * @returns {boolean} Whether a refresh could be triggered.
     */
    refreshDrawer() {
      var drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
        drawerItems.onCartUpdate();
        return true;
      }
      return false;
    }

    /**
     * Show a transient, readable error in Dawn's alert region (instead of
     * stuffing the long message inside the small round add button).
     * @param {string} message
     */
    showError(message) {
      var region = document.getElementById('CartDrawer-CartErrors');
      if (!region) return;
      region.textContent = message;
      window.clearTimeout(this.errorTimeout);
      this.errorTimeout = window.setTimeout(function () {
        region.textContent = '';
      }, 4000);
    }

    /**
     * Add a single variant to the cart and refresh the drawer natively.
     *
     * Robustness notes: only a failed /cart/add.js request counts as a failed
     * add. Everything AFTER the add succeeded (pub/sub subscribers, section
     * re-render) is best-effort — a throw there previously surfaced as a bogus
     * "error while updating your cart" even though the item was in the cart.
     * @param {HTMLButtonElement} button The clicked add button.
     */
    async addVariant(button) {
      var variantId = parseInt(button.dataset.variantId, 10);
      if (!variantId || button.disabled) return;

      // Tag the line item so upsell conversions are visible on the order in
      // Shopify admin (same idea as the old cart app's "__cd_opus_UpSell").
      // The double-underscore prefix keeps the property hidden from customers
      // in the cart, checkout and order status pages.
      var properties = { __daddl_cart_UpSell: String(variantId) };

      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      var cartDrawer = document.querySelector('cart-drawer');
      var sectionIds = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : [];

      var body = { items: [{ id: variantId, quantity: 1, properties: properties }] };
      if (sectionIds.length) {
        body.sections = sectionIds;
        body.sections_url = window.location.pathname;
      }

      var parsed;
      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error('Cart add failed (' + response.status + ')');
        parsed = await response.json();
      } catch (error) {
        // The add itself failed — restore the button and tell the shopper.
        console.error('[daddl-cart-upsells]', error);
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        this.showError(this.strError);
        return;
      }

      // From here on the item IS in the cart; never show a failure to the user.
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'daddl-cart-upsells', cartData: parsed });
        }
      } catch (error) {
        console.error('[daddl-cart-upsells] publish failed', error);
      }

      var rendered = false;
      var sectionsUsable =
        parsed &&
        parsed.sections &&
        sectionIds.length > 0 &&
        sectionIds.every(function (id) {
          return typeof parsed.sections[id] === 'string';
        });

      if (sectionsUsable && cartDrawer && typeof cartDrawer.renderContents === 'function') {
        try {
          cartDrawer.renderContents(parsed);
          rendered = true;
        } catch (error) {
          console.error('[daddl-cart-upsells] renderContents failed', error);
        }
      }

      if (!rendered) {
        // The upsell list itself is not part of the fallback re-fetch, so put
        // the button back; syncVisibility() will hide the card shortly.
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        this.refreshDrawer();
      }
    }
  }

  /* ---------------------------------------------------------------------------
   * <daddl-cart-timer> — session-persistent "reserved" countdown
   * ------------------------------------------------------------------------- */

  var TIMER_STORAGE_KEY = 'daddl:cart-timer:end';

  /**
   * A cosmetic urgency countdown. The end time is persisted in sessionStorage
   * so it survives page navigation within the session, and restarts once it has
   * expired.
   */
  class DaddlCartTimer extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-daddl-timer-value]');
      this.minutes = parseInt(this.dataset.minutes, 10) || 15;

      this.endTime = this.resolveEndTime();
      this.tick = this.tick.bind(this);
      this.tick();
      this.intervalId = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      if (this.intervalId) window.clearInterval(this.intervalId);
    }

    /**
     * Read a still-valid end timestamp from storage, or start a fresh window.
     * @returns {number} Epoch milliseconds when the reservation expires.
     */
    resolveEndTime() {
      var now = Date.now();
      var stored = 0;
      try {
        stored = parseInt(window.sessionStorage.getItem(TIMER_STORAGE_KEY), 10) || 0;
      } catch (error) {
        stored = 0;
      }
      if (stored > now) return stored;

      var fresh = now + this.minutes * 60 * 1000;
      try {
        window.sessionStorage.setItem(TIMER_STORAGE_KEY, String(fresh));
      } catch (error) {
        /* Storage unavailable (private mode); timer still works for the session. */
      }
      return fresh;
    }

    /** Update the mm:ss display; hide the component once the window elapses. */
    tick() {
      var remaining = Math.max(0, this.endTime - Date.now());
      var totalSeconds = Math.floor(remaining / 1000);
      var minutes = Math.floor(totalSeconds / 60);
      var seconds = totalSeconds % 60;

      if (this.output) {
        this.output.textContent =
          String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
      }

      if (remaining <= 0) {
        this.hidden = true;
        if (this.intervalId) window.clearInterval(this.intervalId);
        try {
          window.sessionStorage.removeItem(TIMER_STORAGE_KEY);
        } catch (error) {
          /* no-op */
        }
      }
    }
  }

  /* ---------------------------------------------------------------------------
   * Registration
   * ------------------------------------------------------------------------- */

  defineElement('daddl-cart-rewards', DaddlCartRewards);
  defineElement('daddl-cart-upsells', DaddlCartUpsells);
  defineElement('daddl-cart-timer', DaddlCartTimer);
})();
